import _ from 'lodash';
import moment from 'moment';
import rights from '../../components/rights';
import constants from '$$helpers/constants';
import globalStore from '$$store/main';
import xlsxExport from './xlsxExport';
import taskToXlsxExportHelpers from './xlsxExportHandlers/helpers/taskToXlsxExportHelpers'
import BaselineGrid from '../ganttView/baselines/grid';

const BASELINE_POSTFIX = '_baseline';

class ExcelExporter {
  constructor(ganttInstance, exportSettings) {
    this.ganttInstance = ganttInstance;
    this.exportSettings = exportSettings;
  }

  export() {
    const { baseline } = this.exportSettings;
    const projectStartDate = this.exportSettings.server ? this.ganttInstance.config.start_date : false;
    let rows;
    const costRights = this.ganttInstance.config.multiview_id || rights.project.hasCostRight(this.ganttInstance.config.gantt_id);
    const allTasksRights = this.ganttInstance.config.multiview_id || rights.project.hasRight(this.ganttInstance.config.gantt_id, 'all_tasks');

    const exportOptions = {
      title: this.exportSettings.title,
      isBaseline: !!baseline,
      enablePrice: !!costRights,
      worksheets: [],
      projectStartDate,
      server: this.exportSettings.server || false,
    };

    const tasksData = this.ganttInstance.serialize();
    const baselineModel = exportOptions.isBaseline
      ? globalStore.state.baselines.projectBaselineData[baseline.gantt_id][baseline.event_id]
      : null;

    const resourceProjectDataCache = {};

    tasksData.data.forEach(task => {
      if (task.type === constants.TASK_TYPES.project) {
        return;
      }

      if (!resourceProjectDataCache[task.gantt_id]) {
        resourceProjectDataCache[task.gantt_id] = globalStore.getters['resourcesModel/getResourcesByProjectId'](task.gantt_id);
      }

      task.resources = task.resources.filter(tR => resourceProjectDataCache[task.gantt_id].find(r => r.id === tR.resource_id));
    });

    // let isFilterSet = filtersModel.isSetFilter(filterData);
    const isFilterSet = globalStore.getters['filter/isActive'];

    if (isFilterSet || !allTasksRights) {
      const filteredTasks = tasksData.data.filter(task => this.ganttInstance.isTaskVisible(task.id));

      rows = this.ganttPrepareFilterGanttDataToExport(
        filteredTasks,
        { postfix: BASELINE_POSTFIX },
        { projectStartDate },
        baselineModel,
      );
    } else {
      rows = this.ganttSerializeWithoutCustomButtonsForExport(
        tasksData,
        { postfix: BASELINE_POSTFIX },
        { projectStartDate },
        baselineModel,
      );
    }

    rows = rows.sort((a, b) => a.data.wbsDecimal - b.data.wbsDecimal);

    const splitLevelsToColumns = {
      depth: this.getMaxDepthBylevelColumn(rows, 'level'),
      indentColumn: 'text',
      levelColumn: 'level',
      label: __('export_excel_task_name'),
    };

    let columnsOptions = {
      splitLevelsToColumns,
      excludedColumns: this.exportSettings.server ? ['project', 'type', 'level', 'created', 'creator'] : [],
    };

    if (exportOptions.isBaseline) {
      columnsOptions = {
        splitLevelsToColumns: false,
        compareWithBaselineOptions: {
          excludeBaselineColumns: ['wbs', 'color', 'created', 'actual_cost', 'creator', 'type', 'project', 'level', 'time_tracking'],
          postfix: BASELINE_POSTFIX,
        },
      };
    }

    const columns = taskToXlsxExportHelpers.getTaskColumns(exportOptions, columnsOptions);

    exportOptions.worksheets.push({
      title: exportOptions.title,
      rows,
      columns,
    });

    // console.log(exportOptions);
    xlsxExport.export(exportOptions);
  }

  ganttPrepareFilterGanttDataToExport(ganttFilterData, compareWithBaselineOptions, calculateOptions, baselineModel) {
    const ganttData = [];

    ganttFilterData.forEach((task, index) => {
      task = this.prepareGanttTaskForExport(task, baselineModel);

      const level = this.ganttInstance.calculateTaskLevel(task.id);

      if (task.type === constants.TASK_TYPES.button) {
        return;
      }
      if (task.type === constants.TASK_TYPES.project && task.parent && !level) {
        task.$virtual = true;
      }
      task.$level = level; // added here for xlsx export compability for filterd set
      task.$rendered_type = task.type; // added here for xlsx export compability for filterd set

      if (level === 0) return;
      ganttData.push(taskToXlsxExportHelpers.taskToRow(task, compareWithBaselineOptions, calculateOptions, baselineModel));
    });

    return ganttData;
  }

  ganttSerializeWithoutCustomButtonsForExport(tasks, compareWithBaselineOptions, calculateOptions, baselineModel) {
    const ganttData = [];
    const tasksData = tasks.data;

    tasksData.forEach(task => {
      task = this.prepareGanttTaskForExport(task, baselineModel);

      const level = this.ganttInstance.calculateTaskLevel(task.id);

      task.$level = level;
      if (task.resource && level) {
        task.parent = this.ganttInstance.getChildren(0)[0];
      }
      if (task.type !== constants.TASK_TYPES.button) {
        if (task.parent || !level) {
          if (level === 0) return;
          ganttData.push(taskToXlsxExportHelpers.taskToRow(task, compareWithBaselineOptions, calculateOptions, baselineModel));
        }
      }
    });

    return ganttData;
  }

  prepareGanttTaskForExport(task, baselineModel) {
    const taskForExport = {
      ...this.ganttInstance.getTask(task.id),
      ...task,
    };

    taskForExport.$index = taskForExport.sortorder ? taskForExport.sortorder : -1;

    taskForExport.wbs = this.ganttInstance.getWBSCode(taskForExport);
    taskForExport.wbsFull = this.ganttInstance.getWBSCodeOriginal(taskForExport);
    taskForExport.start_date = _.isString(taskForExport.start_date) ? moment(taskForExport.start_date).toDate() : taskForExport.start_date;

    if (!taskForExport.end_date) {
      taskForExport.end_date = this.ganttInstance.calculateEndDate({
        start_date: taskForExport.start_date,
        duration: taskForExport.duration,
        unit: this.ganttInstance.config.durationData.mode,
        task: taskForExport,
      });
    }

    taskForExport.durationTo = this.ganttInstance.calculateDurationTo(
      moment(taskForExport.start_date).toDate(),
      moment(taskForExport.end_date).toDate(),
      'hour',
      taskForExport.gantt_id
    ).duration;

    taskForExport.predecessors = taskForExport.$target.map(linkId => {
      const dependencyTaskId = this.ganttInstance.getLink(linkId).source;
      const taskMaster = this.ganttInstance.getTask(dependencyTaskId);

      return this.ganttInstance.getWBSCode(taskMaster);
    });

    if (baselineModel) {
      taskForExport.baselineData = BaselineGrid.getBaselineTask(taskForExport.id);
    }

    return taskForExport;
  }

  getMaxDepthBylevelColumn(rows, levelColumn) {
    let depth = 0;

    rows.forEach(row => {
      if (row.data[levelColumn] > depth) {
        depth = row.data[levelColumn];
      }
    });

    return depth;
  }
}

export default ExcelExporter;
